import { createRef, useState, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import R from 'ramda'

import xhr from 'Common/utils/xhr'
import useDebounce from 'Common/hooks/useDebounce'

import Keycaps from 'Images/keycaps.svg'
import {
  Box,
  Button,
  CoverImage,
  Icon,
  Heading,
  Image,
  LoadingOverlay,
} from '@redriverpress/system'

import { REGULAR_EXPRESSIONS } from 'Common/constants/regularExpressions'
import { CodeInput } from '../common/utils/FormUtilComponents'
import useStepper from '../common/utils/useStepper'
import { STD_ONBOARDING_PATH } from '../common/constants'
import {
  useStudentOnboardingContext,
  setYumiClass,
  setClassCode,
} from '../context/onboardingStudentContext'
import Loc from '../common/components/Loc'
import findClassByCode from '../common/endpoints'

const isAndroid = /Android/.test(navigator.userAgent)
const invalidCodeError = 'Invalid invitation code'

export default function EnterClassCode() {
  const history = useHistory()
  const { setNextStep } = useStepper()
  const [
    { locale, socialSso, yumiClass, isInvitation },
    dispatch,
  ] = useStudentOnboardingContext()

  const refs = useRef([...new Array(6)].map(() => createRef()))
  const [invitationCode, setInvitationCode] = useState(
    [...new Array(6)].map(() => '')
  )
  const [signUpError, setSignupError] = useState(null) // check this on flow submission
  const [foundClass, setFoundClass] = useState(false)
  const [loading, setLoading] = useState(false)

  const schema = Yup.object().shape({
    invitationCode: Yup.string()
      .min(6, 'Invalid invitation code')
      .max(6, 'Invalid invitation code')
      .required('Invitation code is required'),
  })

  const handleFindClass = () => {
    const parsedClassCode = invitationCode.join('')
    dispatch(setClassCode(parsedClassCode))
    xhr({
      url: findClassByCode(parsedClassCode),
      method: 'GET',
    })
      .then(res => {
        dispatch(setYumiClass(res.data))
        setLoading(false)
        setFoundClass(true)
        setSignupError(null)
      })
      .catch(() => {
        setLoading(false)
        setFoundClass(false)
        setSignupError(invalidCodeError)
      })
  }

  const handleNextPage = e => {
    e.preventDefault()
    !isInvitation && setNextStep()
    socialSso
      ? history.push({
          pathname: `${STD_ONBOARDING_PATH.signup}/terms-and-conditions`,
        })
      : history.push({
          pathname: `${STD_ONBOARDING_PATH.signup}/what-is-your-name`,
        })
  }

  const debounceHandleFindClass = useDebounce(handleFindClass, 1000)

  useEffect(() => {
    if (!invitationCode.includes('')) {
      setLoading(true)
      debounceHandleFindClass()
    }
  }, [invitationCode])

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      minHeight='90vh'
    >
      <Formik
        initialValues={{ invitationCode: '' }}
        initialErrors={{ invitationCode: '' }}
        validationSchema={schema}
        validateOnChange
        style={{ display: 'flex', flex: '1' }}
      >
        {formik => {
          const { setFieldValue, validateField, touched, errors } = formik

          return (
            <Form autoComplete='off' style={{ display: 'flex', flex: '1' }}>
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                ml={[null, null, socialSso ? 0 : '3rem', null]}
              >
                <Box display='flex' flexDirection='column' alignItems='center'>
                  {/* ----- HEADER ----- */}
                  <Image
                    title='welcome'
                    alt='Welcome'
                    src={Keycaps}
                    height={['60px', null, null, '150px']}
                    mt={[3, null, 0]}
                    mb={[3, null, null, 2]}
                  />
                  <Heading fontSize={[3, null, 4, 5]} mb={[2, null, 3]}>
                    Enter your class code
                  </Heading>
                  {!!locale && (
                    <Loc
                      fontSize={[1, null, 2]}
                      color='gray700'
                      fontWeight='600'
                      copy='Enter your class code'
                      mb={3}
                    />
                  )}

                  {/* ----- INPUT FIELDS ----- */}
                  <Box
                    display='flex'
                    flexDirection={['column', null, 'row']}
                    justifyContent='center'
                    alignItems='center'
                    mt={[4, null, 2]}
                    width={[1, null, '80%', '60%']}
                  >
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      {invitationCode.map((code, i) => {
                        const getBorderColor = () => {
                          if (!touched.invitationCode && !code) {
                            return 'gray300'
                          }
                          if (
                            (errors.invitationCode && !code) ||
                            R.equals(signUpError, invalidCodeError)
                          ) {
                            return 'red700'
                          }

                          return 'gray300'
                        }

                        const onKeyInput = key => {
                          if (R.equals(signUpError, invalidCodeError))
                            setSignupError(null) // clearing invalid code message
                          // if not alphanumeric, onChange will take care of that (backspace, delete, arrows, etc)
                          if (
                            !R.test(
                              REGULAR_EXPRESSIONS.onlyOneAlphanumeric,
                              key
                            )
                          )
                            return

                          const newInvitationCode = R.update(
                            i,
                            key.toUpperCase(),
                            invitationCode
                          )

                          setInvitationCode(newInvitationCode)
                          setFieldValue(
                            'invitationCode',
                            newInvitationCode.join('')
                          )

                          if (i < refs.current.length - 1)
                            refs.current[i + 1].current.focus()
                        }
                        return (
                          <CodeInput
                            // eslint-disable-next-line react/no-array-index-key
                            key={`code_${i}`}
                            mr={[
                              R.lt(i, invitationCode.length - 1) ? 1 : 0,
                              null,
                              R.lt(i, invitationCode.length - 1) ? 2 : 0,
                            ]}
                            ref={refs.current[i]}
                            maxLength={1}
                            borderColor={getBorderColor()}
                            onBlur={() => {
                              validateField('invitationCode')
                            }}
                            // onKeyDown doesn't work on Android, so we need a fallback
                            onInput={e => {
                              if (!isAndroid) return
                              const key = e.nativeEvent.data[0]
                              onKeyInput(key)
                            }}
                            // using key event because max length is 1, so if user presses a key, this replaces the input
                            onKeyDown={({ key }) => {
                              if (isAndroid) return
                              onKeyInput(key)
                            }}
                            onChange={e => {
                              if (R.equals(signUpError, invalidCodeError))
                                setSignupError(null) // clearing invalid code message
                              // if alphanumeric, onKeyDown will take care of that
                              if (
                                R.test(
                                  REGULAR_EXPRESSIONS.onlyOneAlphanumeric,
                                  e.target.value
                                )
                              )
                                return

                              const newInvitationCode = R.update(
                                i,
                                e.target.value,
                                invitationCode
                              )

                              setInvitationCode(newInvitationCode)
                              setFieldValue(
                                'invitationCode',
                                newInvitationCode.join('')
                              )
                            }}
                            value={code}
                            onPaste={e => {
                              if (R.equals(signUpError, invalidCodeError))
                                setSignupError(null) // clearing invalid code message
                              const text = e.clipboardData
                                .getData('Text')
                                .toUpperCase()
                              const newInvitationCode = invitationCode.map(
                                (t, index) =>
                                  R.lt(index, text.length) ? text[index] : t
                              )
                              setInvitationCode(newInvitationCode)
                              setFieldValue(
                                'invitationCode',
                                newInvitationCode.join('')
                              )
                              e.stopPropagation() // to prevent from calling onKeyDown event and pasting a 'V' if cmd/ctrl + V
                              e.preventDefault() // to prevent from actual pasting in case it's larger than 6 chars
                            }}
                          />
                        )
                      })}
                    </Box>
                  </Box>

                  {signUpError && (
                    <Box
                      width={[1, null, '80%', '60%']}
                      mt={2}
                      color='red700'
                      fontWeight='600'
                      fontSize={2}
                    >
                      {signUpError}
                    </Box>
                  )}
                </Box>

                {/* ----- CLASS INFO ----- */}
                {foundClass && yumiClass && (
                  <Box
                    p={[3, null, 2]}
                    mt={4}
                    bg='gray100'
                    borderRadius='3rem'
                    display='flex'
                    flexDirection={['column', null, 'row']}
                    justifyContent='center'
                  >
                    {!yumiClass.invitationDefaultClass && (
                      <Box
                        display='flex'
                        alignItems='center'
                        pr={[0, null, 2]}
                        mb={[2, null, 0]}
                      >
                        <Box
                          borderRadius='circular'
                          size='25px'
                          bg={yumiClass.color}
                          mr={1}
                        />
                        {yumiClass.name}
                      </Box>
                    )}
                    <Box display='flex' alignItems='center' pl={[0, null, 2]}>
                      <CoverImage
                        size='25px'
                        src={yumiClass.teachers[0].avatar_url}
                        mr={1}
                        alt='Teacher Avatar'
                      />
                      {yumiClass.teachers[0].teacher_name}
                    </Box>
                  </Box>
                )}

                {/* ----- FOOTER W/ NAV ----- */}
                <Box
                  width={1}
                  mt='auto'
                  mb={4}
                  display='flex'
                  justifyContent='flex-end'
                >
                  <Button
                    disabled={!foundClass}
                    onClick={handleNextPage}
                    adaptive
                  >
                    Next
                    <Icon ml={2} icon='arrow-right' />
                  </Button>
                </Box>
              </Box>
            </Form>
          )
        }}
      </Formik>
      {loading && <LoadingOverlay />}
    </Box>
  )
}
